import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { QuoteModal } from "@/components/QuoteModal";
import { useState } from "react";

const PrivacyPolicy = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />

      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-8 animate-fade-in">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2025</p>

          <div className="prose prose-lg max-w-none space-y-8">
            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
              <p className="text-muted-foreground mb-4">
                Costal Meridian ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our shipping and logistics services.
              </p>
              <p className="text-muted-foreground">
                By using our services, you consent to the data practices described in this policy. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
              </p>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-semibold mb-3">Personal Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Name, email address, and phone number</li>
                <li>Company name and business registration details</li>
                <li>Shipping addresses and billing information</li>
                <li>Payment card details (processed securely through our payment providers)</li>
                <li>Customs documentation and import/export information</li>
              </ul>
              <h3 className="text-xl font-semibold mb-3">Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>IP address and browser type</li>
                <li>Device information and operating system</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referral sources and search terms</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Process and manage your shipping orders</li>
                <li>Provide tracking information and delivery updates</li>
                <li>Communicate with you about your shipments</li>
                <li>Process payments and prevent fraud</li>
                <li>Comply with customs and regulatory requirements</li>
                <li>Improve our services and website functionality</li>
                <li>Send marketing communications (with your consent)</li>
                <li>Respond to customer service inquiries</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">4. Information Sharing</h2>
              <p className="text-muted-foreground mb-4">
                We may share your information with:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Shipping Partners:</strong> Carriers, freight forwarders, and logistics providers to fulfill your shipments</li>
                <li><strong>Customs Authorities:</strong> As required for international shipments and regulatory compliance</li>
                <li><strong>Payment Processors:</strong> To securely process your transactions</li>
                <li><strong>Service Providers:</strong> Third parties who assist in operating our business</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">5. Data Security</h2>
              <p className="text-muted-foreground mb-4">
                We implement appropriate technical and organizational measures to protect your personal information, including:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>SSL/TLS encryption for data transmission</li>
                <li>Secure servers and firewalls</li>
                <li>Access controls and authentication</li>
                <li>Regular security assessments and audits</li>
                <li>Employee training on data protection</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">6. Your Rights (GDPR)</h2>
              <p className="text-muted-foreground mb-4">
                Under the General Data Protection Regulation (GDPR), you have the right to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Access your personal data</li>
                <li>Rectify inaccurate information</li>
                <li>Request erasure of your data</li>
                <li>Restrict processing of your data</li>
                <li>Data portability</li>
                <li>Object to processing</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">7. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law (e.g., customs records must be kept for 7 years).
              </p>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">8. International Transfers</h2>
              <p className="text-muted-foreground">
                As a global logistics company, we may transfer your data to countries outside the European Economic Area (EEA). We ensure appropriate safeguards are in place, including Standard Contractual Clauses approved by the European Commission.
              </p>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">9. Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                For any questions about this Privacy Policy or to exercise your rights, please contact us:
              </p>
              <div className="bg-secondary p-6 rounded-lg">
                <p className="text-muted-foreground">
                  <strong>Costal Meridian Data Protection Officer</strong><br />
                  555 Anton Blvd suite 1050<br />
                  Costa Mesa, CA 92626, USA<br />
                  Email: support@costalmeridian.com<br />
                  Phone: (714) 798-3650
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
      <QuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} />
    </div>
  );
};

export default PrivacyPolicy;
